import { useCallback, useEffect, useMemo, useState } from 'react'
import { usePersonaContext } from 'app/services/PersonaProvider/PersonaProvider'
import useAppData from 'app/services/hooks/useAppData'
import useLogger from 'lib/util/useLogger'
import getAvatarEditTimestampsById from './actions/getAvatarEditTimestampsById'
import getNfts from './actions/getNfts'
import getSpecialOffers from './actions/getSpecialOffers'
import parseUserData from './actions/parseUserData'

// Logger
const debug = useLogger('Account Data', 'green')

/**
 * The `useAccountData` handles the requesting
 * and managing of user information
 *
 * @param {object?} account - the user account data
 * @returns {object} the context object
 */
function useAccountData (account) {
  const appData = useAppData()
  const { loadStoreData } = usePersonaContext()
  const { isSignedIn } = account
  const [hasAccountData, setHasAccountData] = useState(false)
  const [hasAccountDataError, setHasAccountDataError] = useState(false)
  const [isUpdatingAccountData, setIsUpdatingAccountData] = useState(false)
  const [lastUpdatedAccountDataTimestamp, setLastUpdatedAccountDataTimestamp] =
    useState(null)
  const [accountData, setAccountData] = useState({
    avatars: [],
    allAccessories: [],
    attachedAccessories: [],
    detachedAccessories: []
  })

  /**
   * Load the account data
   */
  const loadAccountData = useCallback(async () => {
    try {
      setIsUpdatingAccountData(true)

      const [nftsRes, storeDataRes, specialOffersRes] = await Promise.all([
        getNfts({ wallet: account?.wallet?.address }),
        loadStoreData(),
        getSpecialOffers()
      ])

      if (![nftsRes, storeDataRes].every(data => data.success)) {
        throw new Error('Data failed to load')
      }

      debug.log('Raw user nfts', nftsRes.data)

      const parsedAccountData = parseUserData({
        nftData: nftsRes.data,
        storeData: storeDataRes.data,
        specialOffersData: specialOffersRes.data,
        categoryConfig: appData?.category_config || {},
        utilityConfig: appData?.sub_utilities || {}
      })

      setHasAccountDataError(false)
      setLastUpdatedAccountDataTimestamp(Date.now())
      setHasAccountData(true)
      setIsUpdatingAccountData(false)
      setAccountData(parsedAccountData)

      debug.log('Account data update success', parsedAccountData)

      return {
        success: true,
        data: parsedAccountData
      }
    } catch (error) {
      debug.error('Failed updating account data:', error)

      setIsUpdatingAccountData(false)
      setHasAccountDataError(true)

      return { success: false }
    }
  }, [account, loadStoreData])

  /**
   * Load an avatars edited timestamps by its id
   *
   * @param {number} avatarId - the avatar id
   * @returns {Promise} resolves on completion
   */
  const loadAvatarEditTimestampsById = useCallback(async avatarId => {
    try {
      const editedRes = await getAvatarEditTimestampsById(avatarId)

      if (!editedRes.success) {
        throw new Error('Data failed to load')
      }

      debug.log('Loaded edited timestamps:', editedRes.data)

      return editedRes
    } catch (error) {
      debug.error('Failed to load timestamps')

      return { success: false }
    }
  }, [])

  /**
   * Effect: When signed in, initiate loading of assets
   * TODO: This can happen on demand instead
   */
  useEffect(() => {
    /* if (!account?.wallet?.address) {
      return
    } */
    if (!isSignedIn) {
      // Not signed in, ignore
      setHasAccountData(false)

      return
    }

    loadAccountData()
  }, [isSignedIn, loadAccountData])

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      hasAccountData,
      hasAccountDataError,
      isUpdatingAccountData,
      lastUpdatedAccountDataTimestamp,
      accountData,
      loadAccountData,
      loadAvatarEditTimestampsById
    }),
    [
      accountData,
      loadAccountData,
      hasAccountData,
      hasAccountDataError,
      lastUpdatedAccountDataTimestamp,
      isUpdatingAccountData,
      loadAvatarEditTimestampsById
    ]
  )

  return ctx
}

// Export
export default useAccountData
